import { Verb, Tense, Conjugation, QuizQuestion } from '../types';
import { verbs } from '../data/verbs';
import { tenses } from '../data/tenses';
import { conjugations } from '../data/conjugations';
import { incorrectOptions } from '../data/incorrectOptions';

// Function to get a random item from an array
const getRandomItem = <T>(array: T[]): T => {
  return array[Math.floor(Math.random() * array.length)];
};

// Function to get random items from an array without duplicates
const getRandomItems = <T>(array: T[], count: number): T[] => {
  const shuffled = [...array].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
};

// Function to get conjugation for a specific verb, tense, and pronoun
const getConjugation = (
  verbInfinitive: string,
  tenseId: string,
  pronoun: 'je' | 'tu' | 'il' | 'nous' | 'vous' | 'ils'
): string | null => {
  const conjugation = conjugations.find(
    (c) => c.verbInfinitive === verbInfinitive && c.tense === tenseId
  );
  
  if (!conjugation) return null;
  
  return conjugation[pronoun];
};

// Function to generate incorrect options for a verb
const generateIncorrectOptions = (
  verb: Verb,
  tenseId: string,
  correctAnswer: string,
  pronoun: 'je' | 'tu' | 'il' | 'nous' | 'vous' | 'ils',
  count: number
): string[] => {
  // Try to get verb-specific incorrect options from our predefined list
  if (incorrectOptions[verb.infinitive] && 
      incorrectOptions[verb.infinitive][tenseId] && 
      incorrectOptions[verb.infinitive][tenseId][pronoun]) {
    // We have specific incorrect options for this verb/tense/pronoun
    return incorrectOptions[verb.infinitive][tenseId][pronoun];
  }
  
  // If we don't have predefined incorrect options, generate them dynamically
  // by using other forms of the same verb
  let potentialOptions: string[] = [];
  
  // Get the conjugation for this verb and tense
  const verbConjugation = conjugations.find(
    c => c.verbInfinitive === verb.infinitive && c.tense === tenseId
  );
  
  if (verbConjugation) {
    // Add other pronouns' conjugations as potential incorrect options
    const pronouns: Array<'je' | 'tu' | 'il' | 'nous' | 'vous' | 'ils'> = ['je', 'tu', 'il', 'nous', 'vous', 'ils'];
    pronouns.forEach(p => {
      if (p !== pronoun && verbConjugation[p]) {
        potentialOptions.push(verbConjugation[p]);
      }
    });
    
    // For reflexive verbs, add versions with wrong reflexive pronouns
    if (verb.infinitive.startsWith('se ')) {
      const baseForm = correctAnswer.replace(/^(me |te |se |nous |vous )/, '');
      const reflexivePronouns = ['me ', 'te ', 'se ', 'nous ', 'vous '];
      
      reflexivePronouns.forEach(rp => {
        const incorrectForm = rp + baseForm;
        if (incorrectForm !== correctAnswer && !potentialOptions.includes(incorrectForm)) {
          potentialOptions.push(incorrectForm);
        }
      });
      
      // Also add the form without any reflexive pronoun
      if (!potentialOptions.includes(baseForm)) {
        potentialOptions.push(baseForm);
      }
    }
    
    // For regular verbs, add common mistakes (wrong endings)
    if (verb.group === 1) { // -er verbs
      if (correctAnswer.endsWith('e')) {
        potentialOptions.push(correctAnswer + 's');
      } else if (correctAnswer.endsWith('es')) {
        potentialOptions.push(correctAnswer.slice(0, -2) + 'e');
      }
    } else if (verb.group === 2) { // -ir verbs
      if (correctAnswer.includes('iss')) {
        potentialOptions.push(correctAnswer.replace('iss', ''));
      }
    }
  }
  
  // Filter out the correct answer
  potentialOptions = potentialOptions.filter(option => option !== correctAnswer);
  
  // If we still don't have enough options, create near-miss forms
  while (potentialOptions.length < count) {
    // Simple typo: add/remove a letter
    if (correctAnswer.length > 2) {
      const typo = correctAnswer.slice(0, -1) + (correctAnswer.slice(-1) === 'e' ? 'a' : 'e');
      if (!potentialOptions.includes(typo)) {
        potentialOptions.push(typo);
      }
    }
    
    // If we still need more, break to avoid infinite loop
    if (potentialOptions.length < count) {
      break;
    }
  }
  
  // Return up to 'count' options, ensuring no duplicates
  return potentialOptions.slice(0, count);
};

// Keep track of recently used verbs to avoid repetition
let lastUsedVerbInfinitive: string | null = null;
let recentlyUsedVerbs: string[] = [];

// List of common verbs that should appear less frequently
const commonVerbs = ['être', 'avoir', 'aller', 'faire', 'dire', 'venir', 'voir', 'pouvoir', 'vouloir'];

// Function to get max occurrences for a verb
const getMaxOccurrencesForVerb = (verbInfinitive: string): number => {
  // Common verbs have a lower max occurrence limit
  return commonVerbs.includes(verbInfinitive) ? 2 : 3;
};

// Function to get a random verb that avoids recently used verbs
// and hasn't appeared more than its max allowed occurrences
const getRandomVerb = (
  verbFrequencyMap: Map<string, number> = new Map(),
  strictLimit: boolean = true
): Verb => {
  // Get verbs that haven't reached their max occurrences
  const availableVerbs = verbs.filter(v => {
    const frequency = verbFrequencyMap.get(v.infinitive) || 0;
    const maxForThisVerb = getMaxOccurrencesForVerb(v.infinitive);
    return frequency < maxForThisVerb;
  });
  
  // If we have available verbs, filter out recently used ones
  if (availableVerbs.length > 0) {
    // First, try to avoid the last 3 verbs used (to prevent consecutive repetition)
    const notRecentlyUsed = availableVerbs.filter(v => !recentlyUsedVerbs.includes(v.infinitive));
    
    if (notRecentlyUsed.length > 0) {
      return getRandomItem(notRecentlyUsed);
    }
    
    // If all available verbs were recently used, at least avoid the last one
    const notLastUsed = availableVerbs.filter(v => v.infinitive !== lastUsedVerbInfinitive);
    if (notLastUsed.length > 0) {
      return getRandomItem(notLastUsed);
    }
    
    // If we have no choice, use any available verb
    return getRandomItem(availableVerbs);
  }
  
  // If strict limit is enforced and no verbs are available under the limit,
  // find verbs with the lowest frequency that weren't recently used
  if (strictLimit) {
    // Find the minimum frequency
    let minFrequency = Infinity;
    verbs.forEach(v => {
      const frequency = verbFrequencyMap.get(v.infinitive) || 0;
      if (frequency < minFrequency) {
        minFrequency = frequency;
      }
    });
    
    // Get all verbs with the minimum frequency
    const leastUsedVerbs = verbs.filter(v => {
      const frequency = verbFrequencyMap.get(v.infinitive) || 0;
      return frequency === minFrequency;
    });
    
    // Try to avoid recently used verbs among the least used ones
    const notRecentlyUsedLeastUsed = leastUsedVerbs.filter(v => !recentlyUsedVerbs.includes(v.infinitive));
    
    if (notRecentlyUsedLeastUsed.length > 0) {
      return getRandomItem(notRecentlyUsedLeastUsed);
    }
    
    // If all least used verbs were recently used, at least avoid the last one
    const notLastUsedLeastUsed = leastUsedVerbs.filter(v => v.infinitive !== lastUsedVerbInfinitive);
    if (notLastUsedLeastUsed.length > 0) {
      return getRandomItem(notLastUsedLeastUsed);
    }
    
    return getRandomItem(leastUsedVerbs);
  }
  
  // Fallback: use any verb except recently used ones
  const notRecentlyUsed = verbs.filter(v => !recentlyUsedVerbs.includes(v.infinitive));
  if (notRecentlyUsed.length > 0) {
    return getRandomItem(notRecentlyUsed);
  }
  
  // Ultimate fallback: use any verb except the last one
  const notLastUsed = verbs.filter(v => v.infinitive !== lastUsedVerbInfinitive);
  return notLastUsed.length > 0 ? getRandomItem(notLastUsed) : getRandomItem(verbs);
};

// Function to generate a quiz question
export const generateQuizQuestion = (
  selectedTenses: string[],
  verbFrequencyMap: Map<string, number> = new Map(),
  maxVerbOccurrences: number = 3
): QuizQuestion => {
  // Get a random tense from the selected tenses
  const randomTenseId = getRandomItem(selectedTenses);
  const randomTense = tenses.find(t => t.id === randomTenseId) || tenses[0];
  
  // Get a random pronoun
  const pronouns: Array<'je' | 'tu' | 'il' | 'nous' | 'vous' | 'ils'> = ['je', 'tu', 'il', 'nous', 'vous', 'ils'];
  const randomPronoun = getRandomItem(pronouns);
  
  // Get a random verb (avoiding recently used verbs and respecting max occurrences)
  let randomVerb = getRandomVerb(verbFrequencyMap, true);
  
  // Get the correct conjugation
  let correctAnswer = getConjugation(randomVerb.infinitive, randomTenseId, randomPronoun);
  
  // If conjugation is missing, try to find another verb that has a conjugation
  let attempts = 0;
  const maxAttempts = 10; // Prevent infinite loops
  
  while (!correctAnswer && attempts < maxAttempts) {
    // Try another verb, still avoiding the last used one if possible
    randomVerb = getRandomVerb();
    correctAnswer = getConjugation(randomVerb.infinitive, randomTenseId, randomPronoun);
    attempts++;
  }
  
  // Fallback if we still don't have a valid conjugation
  if (!correctAnswer) {
    // Use a common verb that we know has conjugations
    randomVerb = verbs.find(v => v.infinitive === 'être') || verbs[0];
    correctAnswer = getConjugation(randomVerb.infinitive, randomTenseId, randomPronoun) || 'suis'; // Fallback value
  }
  
  // Update the last used verb for the next question
  lastUsedVerbInfinitive = randomVerb.infinitive;
  
  // Generate incorrect options
  const incorrectOptions = generateIncorrectOptions(randomVerb, randomTenseId, correctAnswer, randomPronoun, 3);
  
  // Ensure all options are unique
  const uniqueIncorrectOptions = Array.from(new Set(incorrectOptions));
  
  // If we have fewer than 3 unique incorrect options, generate more
  while (uniqueIncorrectOptions.length < 3) {
    // Create a variation of the correct answer
    const variation: string = correctAnswer + (Math.random() > 0.5 ? 's' : 'x');
    if (!uniqueIncorrectOptions.includes(variation) && variation !== correctAnswer) {
      uniqueIncorrectOptions.push(variation);
    }
  }
  
  // Combine and shuffle all options
  const allOptions = [correctAnswer, ...uniqueIncorrectOptions.slice(0, 3)].sort(() => 0.5 - Math.random());
  
  return {
    verb: randomVerb,
    tense: randomTense,
    pronoun: randomPronoun,
    correctAnswer,
    options: allOptions
  };
};

// Function to generate a set of quiz questions
export const generateQuiz = (selectedTenses: string[], count: number): QuizQuestion[] => {
  const questions: QuizQuestion[] = [];
  
  // Reset tracking variables at the start of a new quiz
  lastUsedVerbInfinitive = null;
  recentlyUsedVerbs = [];
  
  // Create a map to track how many times each verb appears in the quiz
  const verbFrequencyMap = new Map<string, number>();
  const strictLimit = true; // Enforce strict limits on verb frequency
  
  // Debug: keep track of verb frequencies for logging
  const verbCounts: Record<string, number> = {};
  const consecutiveVerbCounts: Record<string, number> = {};
  
  for (let i = 0; i < count; i++) {
    // Generate a question with the current verb frequency map
    const question = generateQuizQuestion(selectedTenses, verbFrequencyMap);
    
    // Update the frequency map with the verb that was used
    const verbInfinitive = question.verb.infinitive;
    const currentCount = verbFrequencyMap.get(verbInfinitive) || 0;
    const newCount = currentCount + 1;
    verbFrequencyMap.set(verbInfinitive, newCount);
    
    // Update recently used verbs list (keep only the last 3)
    recentlyUsedVerbs.unshift(verbInfinitive);
    if (recentlyUsedVerbs.length > 3) {
      recentlyUsedVerbs.pop();
    }
    
    // Update the last used verb
    lastUsedVerbInfinitive = verbInfinitive;
    
    // Debug: track verb counts
    verbCounts[verbInfinitive] = (verbCounts[verbInfinitive] || 0) + 1;
    
    // Track consecutive occurrences for debugging
    if (i > 0 && questions[i-1].verb.infinitive === verbInfinitive) {
      consecutiveVerbCounts[verbInfinitive] = (consecutiveVerbCounts[verbInfinitive] || 1) + 1;
    }
    
    // Log warning if a verb is being used more than its limit
    const maxForThisVerb = getMaxOccurrencesForVerb(verbInfinitive);
    if (newCount > maxForThisVerb) {
      console.warn(`Warning: Verb '${verbInfinitive}' is being used ${newCount} times (exceeds limit of ${maxForThisVerb})`);
    }
    
    questions.push(question);
  }
  
  // Debug: log verb frequencies and consecutive occurrences
  console.log('Verb frequencies in quiz:', verbCounts);
  console.log('Consecutive verb occurrences:', consecutiveVerbCounts);
  
  return questions;
};
